import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Logo } from '../components/Logo';
import { 
  Check,
  ChevronLeft,
  CreditCard,
  Loader2,
  CheckCircle2,
  XCircle
} from 'lucide-react';
import { toast } from 'sonner';

const SubscriptionPage = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await api.get('/subscriptions/plans');
        setPlans(response.data);
      } catch (error) {
        console.error('Failed to fetch plans:', error);
      }
    };

    fetchPlans();
  }, [api]);

  // Poll for payment status
  useEffect(() => {
    if (sessionId) {
      const checkPayment = async () => {
        setCheckingPayment(true);
        let attempts = 0;
        const maxAttempts = 10;

        const poll = async () => {
          try {
            const response = await api.get(`/subscriptions/status/${sessionId}`);
            if (response.data.payment_status === 'paid') {
              setPaymentStatus('success');
              toast.success('Subscription activated successfully!');
              return true;
            } else if (response.data.status === 'expired') {
              setPaymentStatus('expired');
              return true;
            }
            return false;
          } catch (error) {
            console.error('Payment check error:', error);
            return false;
          }
        };

        while (attempts < maxAttempts) {
          const done = await poll();
          if (done) break;
          await new Promise(resolve => setTimeout(resolve, 2000));
          attempts++;
        }

        setCheckingPayment(false);
      };

      checkPayment();
    }
  }, [sessionId, api]);

  const handleSubscribe = async (planId) => {
    setLoading(true);
    try {
      const origin = window.location.origin;
      const response = await api.post(`/subscriptions/checkout?plan_id=${planId}`, null, {
        headers: { origin }
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error('Failed to start checkout');
      console.error('Checkout error:', error);
    } finally {
      setLoading(false);
    }
  };

  const isSubscribed = user?.subscription_status === 'active';

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo linkTo="/dashboard" size="default" />

          <Link to="/dashboard">
            <Button variant="ghost" className="flex items-center gap-2 text-zinc-400 hover:text-white">
              <ChevronLeft className="w-4 h-4" />
              Back to Dashboard
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Payment Status */}
        {checkingPayment && (
          <div className="mb-8 p-6 bg-teal-400/10 border border-teal-400/30 rounded-sm flex items-center gap-4">
            <Loader2 className="w-6 h-6 text-teal-400 animate-spin" />
            <div>
              <h3 className="font-bold text-teal-400">Processing Payment</h3>
              <p className="text-zinc-400 text-sm">Please wait while we confirm your subscription...</p>
            </div>
          </div>
        )}

        {paymentStatus === 'success' && (
          <div className="mb-8 p-6 bg-green-500/10 border border-green-500/30 rounded-sm flex items-center gap-4">
            <CheckCircle2 className="w-6 h-6 text-green-500" />
            <div>
              <h3 className="font-bold text-green-500">Payment Successful!</h3>
              <p className="text-zinc-400 text-sm">Your subscription is now active. Enjoy unlimited classes!</p>
            </div>
          </div>
        )}

        {paymentStatus === 'expired' && (
          <div className="mb-8 p-6 bg-red-500/10 border border-red-500/30 rounded-sm flex items-center gap-4">
            <XCircle className="w-6 h-6 text-red-500" />
            <div>
              <h3 className="font-bold text-red-500">Payment Expired</h3>
              <p className="text-zinc-400 text-sm">The checkout session has expired. Please try again.</p>
            </div>
          </div>
        )}

        {/* Current Status */}
        {isSubscribed && (
          <div className="mb-8 p-6 bg-[#121212] border border-zinc-800 rounded-sm">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                <Check className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <h3 className="font-bold text-green-500">Active Subscription</h3>
                <p className="text-zinc-400">You have full access to all classes and features.</p>
              </div>
            </div>
          </div>
        )}

        {/* Page Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {isSubscribed ? 'MANAGE SUBSCRIPTION' : 'CHOOSE YOUR PLAN'}
          </h1>
          <p className="text-zinc-500 text-lg max-w-xl mx-auto">
            {isSubscribed 
              ? 'You currently have an active subscription.'
              : 'Unlock unlimited access to live and on-demand classes'
            }
          </p>
        </div>

        {/* Plans */}
        <div className="grid md:grid-cols-2 gap-8">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative rounded-sm p-8 ${
                plan.id === 'yearly' 
                  ? 'bg-teal-400/10 border-2 border-teal-400' 
                  : 'bg-[#121212] border border-zinc-800'
              }`}
            >
              {plan.id === 'yearly' && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-teal-400 text-sm font-bold uppercase rounded-sm">
                  Best Value
                </span>
              )}
              
              <h3 className="text-2xl font-bold mb-2">{plan.name.toUpperCase()}</h3>
              <div className="flex items-baseline gap-1 mb-6">
                <span className="text-4xl font-bold">${plan.price}</span>
                <span className="text-zinc-500">/{plan.interval}</span>
              </div>

              <ul className="space-y-4 mb-8">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-teal-400 flex-shrink-0" />
                    <span className="text-zinc-300">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button
                onClick={() => handleSubscribe(plan.id)}
                disabled={loading || isSubscribed}
                className={`w-full h-12 font-bold uppercase tracking-wider ${
                  plan.id === 'yearly' 
                    ? 'bg-teal-400 text-white hover:bg-teal-500' 
                    : 'bg-white text-black hover:bg-zinc-200'
                }`}
                data-testid={`subscribe-${plan.id}`}
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : isSubscribed ? (
                  'Already Subscribed'
                ) : (
                  <>
                    <CreditCard className="w-5 h-5 mr-2" />
                    Subscribe Now
                  </>
                )}
              </Button>
            </div>
          ))}
        </div>

        {/* Features */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold text-center mb-8">ALL PLANS INCLUDE</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: 'Unlimited Classes', desc: 'Access all live and on-demand content' },
              { title: 'Progress Tracking', desc: 'Monitor your fitness journey' },
              { title: 'Expert Instructors', desc: 'Learn from certified professionals' },
              { title: 'Mobile Access', desc: 'Stream from any device' },
              { title: 'New Classes Weekly', desc: 'Fresh content added regularly' },
              { title: 'Cancel Anytime', desc: 'No long-term commitments' }
            ].map((feature, i) => (
              <div key={i} className="flex items-start gap-3">
                <Check className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-bold">{feature.title}</h4>
                  <p className="text-sm text-zinc-500">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold text-center mb-8">FREQUENTLY ASKED QUESTIONS</h2>
          <div className="space-y-4">
            {[
              { q: 'Can I cancel my subscription?', a: 'Yes, you can cancel anytime from your account settings. Your access will continue until the end of your billing period.' },
              { q: 'Do you offer a free trial?', a: 'Yes! New members get a 7-day free trial to explore all features.' },
              { q: 'What payment methods do you accept?', a: 'We accept all major credit cards through our secure payment processor.' },
              { q: 'Can I switch between plans?', a: 'Yes, you can upgrade or downgrade your plan at any time. Changes take effect at the start of your next billing cycle.' }
            ].map((faq, i) => (
              <div key={i} className="bg-[#121212] border border-zinc-800 rounded-sm p-6">
                <h4 className="font-bold mb-2">{faq.q}</h4>
                <p className="text-zinc-400 text-sm">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default SubscriptionPage;
